import React, { memo, useRef, useMemo } from "react";
import { Platform, Pressable, TouchableOpacity, View } from "react-native";
import { Image as ExpoImage } from "expo-image";
import styled from "styled-components/native";
import { useRecyclingState } from "@shopify/flash-list";
import VDBIcon from "@components/icons/VDBIcon";
import { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import Loader from "@components/atoms/Loader";
import CustomWeb from "@components/atoms/Webview";
import EmptyImagePreview from "../../GalleryView/utils/EmptyImage/EmptyImagePreview";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { FALLBACK_IMAGE_ASSET } from "@utils/product/searchResultConstants";
import { isPair } from "@utils/product/productValidators";
import { getShapeValue } from "../../GalleryView/utils/EmptyImage/utils";
import {
  getImageSource,
  getIOSImageProps,
  ImageSizePreset,
} from "@utils/image/imageOptimizer";

interface ImageSectionProps {
  item: any;
  imageUri: string;
  showVideoIcon: boolean;
  showErrorImage: boolean;
  showImageContainer: boolean;
  playVideo: boolean;
  httpErrorIcon: boolean;
  videoNotAvailable: boolean;
  onError: () => void;
  onVideoPress: () => void;
  onNoVideoAvailable: () => void;
  onVideoErrorCallback: () => void;
  onItemClick: () => void;
  theme: any;
  createItemDetailLink?: (params: any) => string;
  preventLeftClickWeb?: (e: any) => void;
  priceMode: string;
  productPrefix: string;
}

const IMAGE_STYLE = {
  width: "100%" as const,
  aspectRatio: 1,
  borderTopLeftRadius: 6,
  borderTopRightRadius: 6,
};

const ImageComponent = memo(
  ({ imageUri, onError, showVideo, itemId }: any) => {
    const { disableImageZoom } = useUserInfo();
    const [loading, setLoading] = useRecyclingState(
      false,
      [itemId, imageUri],
      () => {}
    );
    const loadEnded = useRef(false);

    const handleLoadEnd = () => {
      loadEnded.current = true;
      setLoading(false);
    };

    const handleLoadStart = () => {
      if (!loadEnded.current) setLoading(true);
    };

    const rawUri = imageUri || (showVideo ? "" : FALLBACK_IMAGE_ASSET);
    const source = useMemo(
      () => getImageSource(rawUri, ImageSizePreset.GRID),
      [rawUri]
    );

    return (
      <>
        {loading && <Loader size={20} />}
        <ExpoImage
          style={IMAGE_STYLE}
          onLoadStart={handleLoadStart}
          onLoadEnd={handleLoadEnd}
          source={source}
          recyclingKey={`img-${itemId}`}
          contentFit={disableImageZoom() ? "contain" : "cover"}
          {...getIOSImageProps()}
          onError={onError}
        />
      </>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.imageUri === nextProps.imageUri &&
      prevProps.showVideo === nextProps.showVideo &&
      prevProps.itemId === nextProps.itemId &&
      prevProps.onError === nextProps.onError
    );
  }
);

ImageComponent.displayName = "ImageComponent";

const ImageSection = memo<ImageSectionProps>(
  ({
    item,
    imageUri,
    showVideoIcon,
    showErrorImage,
    showImageContainer,
    playVideo,
    httpErrorIcon,
    videoNotAvailable,
    onError,
    onVideoPress,
    onNoVideoAvailable,
    onVideoErrorCallback,
    onItemClick,
    theme,
    createItemDetailLink,
    preventLeftClickWeb,
    priceMode,
    productPrefix,
  }) => {
    const injectedCSS = `
      .default-view, .main {
        display: table;
        margin: 0;
      }
      .divbtnpos,.hPHUXe, .sliderHeight {
        display: none !important;
      }

      .frameContainer, .V360-instance {
        height: auto !important;
        aspect-ratio: 1 !important;
      }
    `;

    const renderVideoIcon = () => (
      <VideoPlayIconContainer theme={theme}>
        <VDBIcon
          color={theme["over-04"]}
          size={40}
          variant={IconsVariant.VDBCustom}
          name={VDBIcons.PLAY}
        />
      </VideoPlayIconContainer>
    );

    const renderFallbackImage = () => (
      <FallbackImageContainer>
        <EmptyImagePreview
          shape={getShapeValue(item)}
          size={theme.isLargeScreen ? 199.04 : 145.96}
          borderRadius={0}
          isPair={isPair(item) ?? false}
        />
      </FallbackImageContainer>
    );

    const renderVideoUnavailableIcon = () => (
      <VDBIcon
        color={theme["core-06"]}
        size={128}
        variant={IconsVariant.VDBCustom}
        name={VDBIcons.VIDEO_UNAVAILABLE}
      />
    );

    const renderImageOrVideoBackground = () => {
      if (showErrorImage) {
        return renderFallbackImage();
      } else if (showImageContainer) {
        return (
          <ImageComponent
            key={`${item?.id ?? "item"}-${imageUri}`}
            imageUri={imageUri}
            onError={onError}
            showVideo={showVideoIcon}
            itemId={item?.id}
          />
        );
      } else {
        return (
          <VideoBackgroundContainer theme={theme}>
            {renderVideoIcon()}
          </VideoBackgroundContainer>
        );
      }
    };

    if (videoNotAvailable) {
      return (
        <VideoContainer
          noVideoAvailable={true}
          theme={theme}
          {...(Object?.keys?.(item)?.length < 1 && {
            style: { pointerEvents: "none" },
          })}
          onPress={onNoVideoAvailable}
        >
          {renderVideoUnavailableIcon()}
        </VideoContainer>
      );
    } else if (playVideo && item?.video_url) {
      return (
        <VideoContainer
          httpErrorIcon={httpErrorIcon}
          theme={theme}
          {...(Object?.keys?.(item)?.length < 1 && {
            style: { pointerEvents: "none" },
          })}
          onPress={() => {
            if (httpErrorIcon) {
              onNoVideoAvailable();
            }
          }}
        >
          {httpErrorIcon ? (
            renderVideoUnavailableIcon()
          ) : (
            <CustomWeb
              url={item?.video_url}
              injectedCSS={injectedCSS}
              errorCallback={onVideoErrorCallback}
              onMessage={() => null}
              allowScaleScript={false}
              isClickable={theme.isLargeScreen ? true : false}
            />
          )}
        </VideoContainer>
      );
    } else {
      return (
        <ImageResourceContainer>
          {showVideoIcon ? (
            <Pressable
              disabled={Object?.keys?.(item)?.length < 1}
              onPress={onVideoPress}
              style={{ position: "relative" }}
            >
              <ImageComponent
                key={`${item?.id ?? "item"}-${imageUri}`}
                imageUri={imageUri}
                onError={onError}
                showVideo={showVideoIcon}
                itemId={item?.id}
              />
              {showVideoIcon && (
                <View
                  style={{
                    pointerEvents: "none",
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: [{ translateX: -45 }, { translateY: -45 }],
                  }}
                >
                  {renderVideoIcon()}
                </View>
              )}
            </Pressable>
          ) : (
            <TouchableOpacity
              activeOpacity={1}
              {...(Object?.keys?.(item)?.length < 1 && {
                style: { pointerEvents: "none" },
              })}
              onPress={onItemClick}
            >
              <AnchorTag
                href={
                  Platform.OS === "web" && createItemDetailLink
                    ? createItemDetailLink({
                        productID: item?.id,
                        productType: item?.type,
                        priceMode,
                        productPrefix,
                      })
                    : undefined
                }
                onClick={
                  Platform.OS === "web" && preventLeftClickWeb
                    ? preventLeftClickWeb
                    : undefined
                }
              >
                {renderImageOrVideoBackground()}
              </AnchorTag>
            </TouchableOpacity>
          )}
        </ImageResourceContainer>
      );
    }
  },
  (prev, next) => {
    return (
      prev.imageUri === next.imageUri &&
      prev.showVideoIcon === next.showVideoIcon &&
      prev.showErrorImage === next.showErrorImage &&
      prev.showImageContainer === next.showImageContainer &&
      prev.playVideo === next.playVideo &&
      prev.httpErrorIcon === next.httpErrorIcon &&
      prev.videoNotAvailable === next.videoNotAvailable &&
      prev.item?.id === next.item?.id &&
      prev.item?.video_url === next.item?.video_url
    );
  }
);

ImageSection.displayName = "ImageSection";

const VideoContainer = styled(TouchableOpacity)<{
  httpErrorIcon?: boolean;
  noVideoAvailable?: boolean;
  theme: any;
}>`
  width: 100%;
  aspect-ratio: 1;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  position: absolute;
  ${({ httpErrorIcon, noVideoAvailable, theme }) =>
    (httpErrorIcon || noVideoAvailable) &&
    `
      align-items: center;
      justify-content: center;
      background-color: ${theme["core-01"]};
    `}
`;

const ImageResourceContainer = styled.View`
  width: 100%;
  background-color: ${({ theme }) => theme["core-01"]};
  position: absolute;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
`;

const FallbackImageContainer = styled.View`
  aspect-ratio: 1;
  background-color: #ffffff;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  justify-content: center;
  align-items: center;
  flex: 1;
`;

const VideoPlayIconContainer = styled.View<any>`
  height: 88px;
  width: 88px;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme["over-02"]};
  border-radius: 44px;
`;

const VideoBackgroundContainer = styled.View<any>`
  width: 100%;
  aspect-ratio: 1;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  background-color: ${({ theme }) => theme["core-02"]};
  justify-content: center;
  align-items: center;
`;

const AnchorTag =
  Platform.OS === "web"
    ? styled.View.attrs(() => ({ as: "a" as any }))`
        text-decoration: none;
        display: flex;
      `
    : View;

export default ImageSection;
